import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, switchMap, tap, catchError, map, throwError } from 'rxjs';
import { Profile } from '../core/models';
import { SERVER_URL } from '@src/environments/environment';
import Session from 'supertokens-web-js/recipe/session';

const PROFILE_STORAGE_KEY = 'user_profile';

interface SignInRequest {
  formFields: {
    id: string;
    value: string;
  }[];
}

interface SignInResponse {
  status: string;
  user: {
    token?: string;
  };
}

@Injectable({providedIn: 'root'})
export class AuthService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private profileSubject = new BehaviorSubject<Profile | null>(this.loadStoredProfile());

  isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  profile$ = this.profileSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    // Check if user is already authenticated (e.g., from localStorage)
    const token = localStorage.getItem('token');
    if (token) {
      this.isAuthenticatedSubject.next(true);
    }
  }

  async doesSessionExist() {
    const currentProfile = this.profileSubject.getValue();
    if (currentProfile) {
      return true
    }
    const auth = await Session.doesSessionExist();
    if (auth) {
      if (await Session.doesSessionExist()) {
        let validationErrors = await Session.validateClaims();
        if (validationErrors.length === 0) {
          return true
        }
      }
      return false
    }
    return false
  }

  signIn(email: string, password: string): Observable<any> {
    const request: SignInRequest = {
      formFields: [
        {
          id: "password",
          value: password
        },
        {
          id: "email",
          value: email
        }
      ]
    };

    return this.http.post<SignInResponse>(`${SERVER_URL}/signin`, request, { observe: 'response' }).pipe(
      switchMap(response => {
        if (response.body?.status.toLowerCase() === 'ok') {          
          this.isAuthenticatedSubject.next(true);
          
          // Load profile
          return this.loadProfile().pipe(
            tap(() => {
              this.router.navigate(['/dashboard']);
            })
          );
        } else {
          throw new Error('Authentication failed');
        }
      })
    );
  }

  loadProfile(): Observable<Profile> {
    return this.http.get<Profile>(`${SERVER_URL}/profile`).pipe(
      tap(profile => {
        this.saveProfile(profile);
      }),
      catchError(error => {
        console.error('Profile load error:', error);
        return throwError(() => error);
      })
    );
  }

  getCurrentProfile(): Profile | null {
    return this.profileSubject.getValue();
  }

  updateProfile(profile: Profile): Observable<Profile> {
    return this.http.put<Profile>(`${SERVER_URL}/user`, profile).pipe(
      tap(updatedProfile => {
        this.saveProfile(updatedProfile);
      }),
      catchError(error => {
        console.error('Profile update error:', error);
        return throwError(() => error);
      })
    );
  }

  async signOut() {
    // First, clear local storage and cookies
    localStorage.removeItem('token');
    localStorage.removeItem(PROFILE_STORAGE_KEY);
    
    // Clear profile data
    this.clearProfile();
    this.isAuthenticatedSubject.next(false);
    
    // Try to sign out from SuperTokens
    try {
      // Use the signOut method from SuperTokens
      await Session.signOut();
    } catch (error) {
      console.log('Error during SuperTokens signout:', error);
      // Continue with navigation even if the server request fails
    }
    
    // Navigate to login page regardless of signout success
    this.router.navigate(['/login']);
  }

  private loadStoredProfile(): Profile | null {
    const storedProfile = localStorage.getItem(PROFILE_STORAGE_KEY);
    
    if (storedProfile) {
      try {
        const profile = JSON.parse(storedProfile);
        return profile;
      } catch (error) {
        return null;
      }
    }
    return null;
  }

  private saveProfile(profile: Profile): void {
    localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile));
    this.profileSubject.next(profile);
  }

  private clearProfile(): void {
    localStorage.removeItem(PROFILE_STORAGE_KEY);
    this.profileSubject.next(null);
  }

  requestPasswordReset(email: string): Observable<string> {
    // Custom endpoint for password reset request
    // Add a parameter to indicate we don't want tenant info in the reset link
    return this.http.post<{message: string}>(`${SERVER_URL}/reset-password`, { 
      email,
      excludeTenantId: true  // Add this flag to tell the backend not to include tenantId in the reset link
    }).pipe(
      map(response => response.message),
      catchError(error => {
        console.error('Password reset request error:', error);
        // Extract the message from the error response if available
        const errorMessage = error.error?.message || 'Failed to send password reset email. Please try again.';
        return throwError(() => new Error(errorMessage));
      })
    );
  }


  resetPassword(token: string, newPassword: string): Observable<any> {
    // Only send the token and password to the API, no tenant info
    return this.http.post(`${SERVER_URL}/new-password`, { 'token': token, 'password': newPassword }).pipe(
      catchError(error => {
        console.error('Password reset error:', error);
        // Extract the message from the error response if available
        const errorMessage = error.error?.message || 'Failed to reset password. Please try again.';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }
}
